/**
 * THE CHARTER, THE DECISIONS, AND SCOUT'S OWN PROPOSALS.
 *
 * WHY ONE MODULE. These three are the same question asked at three distances:
 * what is Scout governed by, what has it decided under that governance, and
 * what does it think should change. What must not drift between them is exactly
 * what they share — the line between WHAT IS ENFORCED BY CODE and WHAT IS ONLY
 * WRITTEN DOWN — so the sentence lives here once rather than three times.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS SURFACE IS BUILT ON, AND IT IS THE WHOLE POINT
 * ---------------------------------------------------------------------------
 *
 * A SIGNED CHARTER IS CARRIED INTO THE PROMPT, NOT EXECUTED BY THE RUNTIME.
 * `scoutCharterBlock` is prepended to the system prompt of FREE_TEXT reasoning
 * calls, and the Owner's signed text reaches the model verbatim. Four things in
 * that text are also READ BY CODE, because they are a grammar rather than
 * prose — a plain `DECISION:` line becomes a durable record, `DECISION: CLOSE`
 * performs the same reviewed close transition the Owner's CLI performs,
 * `DECISION: OPEN` registers an episode and spends nothing, and
 * `DECISION REVERSED:` marks a named row reversed. Everything else the charter
 * says — its tiers, its "ask once with a default" rule, its prohibitions — is
 * INSTRUCTION TO A MODEL. No validator, guard or gate reads it.
 *
 * SO THIS VIEW NEVER RENDERS A CHARTER CLAUSE AS A RUNTIME GUARANTEE. It shows
 * the signed text as what it is, states which four acts have code behind them,
 * and says plainly that the rest is prompt text. A product that showed the
 * taxonomy in a "governing rules" box would be claiming an enforcement nobody
 * built.
 *
 * AND IT SHOWS THE TEXT VERBATIM, INCLUDING WHAT IS WRONG WITH IT. The signed
 * document's own first line reads "DRAFT FOR OWNER SIGNATURE. Not in force." —
 * because those are the bytes the Owner signed, and this view will not launder
 * them. A charter rendered with its contradictions removed is not the charter.
 *
 * NOTHING HERE RUNS A MODEL, AND NOTHING HERE CHANGES ANYTHING. Every read is a
 * GET. Signing a charter, reversing a decision, recording feedback and recording
 * an outcome are all POSTs that exist and all belong to the Owner's own path —
 * the CLI and the terminal. SCOUT HAS NO HANDS, and a page that grew a "sign
 * this" or "reverse that" button would be the beginning of it having some.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable, expectsMembers } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { navigate, route } from '../router';
import { DiscussLink } from './discuss';
import { PageHeader, Technical } from './about';

/** HOW MANY EACH LIST ASKS FOR, named here because the view must SAY what it
 *  asked for — a bound a view cannot state is a bound it cannot be honest
 *  about. */
const PAGE = { decisions: 50 } as const;

interface CharterVersion {
	charterId: string;
	version: number;
	signedAt: string;
	contentSha256: string;
	note: string | null;
}

interface CharterPayload {
	found: boolean;
	charter: {
		charterId: string;
		version: number;
		signedAt: string;
		text: string;
		contentSha256: string;
		note: string | null;
	} | null;
	versions?: CharterVersion[];
}

interface DecisionRecord {
	decisionId: string;
	statement: string;
	basis: string;
	episodeId: string | null;
	outcome: string | null;
	createdAt: string;
	reversedAt: string | null;
	reversalNote: string | null;
}

interface CandidateReview {
	decision: string;
	reason: string;
	actor: string;
	at: string;
}

interface ImplementationEvidence {
	commitSha: string | null;
	deployedVersion: string | null;
	configRef: string | null;
	changeRef: string | null;
	description: string;
	actor: string;
	at: string;
}

interface VerificationEvidence {
	method: string;
	testRef: string | null;
	evaluationRef: string | null;
	beforeAfter: string | null;
	result: string;
	actor: string;
	at: string;
}

interface StageReading {
	reached: string;
	establishes: string;
	doesNotEstablish: string;
	nextRequires: string | null;
	unaccountedFor: string[];
}

interface Proposal {
	selfImprovementId: string;
	observedWeakness: string;
	critiqueRefs: string[];
	evidenceRefs: string[];
	expectedBenefit: string;
	expectedBenefitClass: string;
	alternatives: string[];
	confidence: string;
	evaluationMethod: string;
	applicability: string[];
	candidateId: string | null;
	authoritySeeking: boolean;
	authorityReason: string | null;
	createdAt: string;
	/** THE LEDGER'S OWN RECORD AND LIFE — present on every response from the
	 *  current build, optional so a stale cached payload cannot crash a view. */
	review?: CandidateReview | null;
	implementationEvidence?: ImplementationEvidence | null;
	verificationEvidence?: VerificationEvidence | null;
	lifecycle?: string;
	stage?: StageReading | null;
	linkedEpisodes?: string[];
}

interface Critique {
	critiqueId: string;
	kind: string;
	statement: string;
	evidenceRefs: string[];
	confidence: string;
	resolutionTest: string;
	createdAt: string;
	resolvedAt: string | null;
}

function formatWhen(value: string): string {
	const at = new Date(value);
	if (Number.isNaN(at.getTime())) return value;
	return new Intl.DateTimeFormat(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	}).format(at);
}

function Bounded({ shown, noun }: { shown: number; noun: string }) {	return (
		<p class="countline small">
			Showing <span class="count">{shown}</span> {noun}
			<span class="muted">
				{' '}
				— the newest this read asked for. A full page is followed by Load more where the route
				provides a continuation; the count is this page's, not the corpus's.
			</span>
		</p>
	);
}

/** THE LINE, STATED ONCE, RENDERED WHEREVER IT APPLIES. */
function EnforcementNote() {
	return (
		<section class="panel not-known" aria-labelledby="enforcement-heading">
			<h2 id="enforcement-heading">What is enforced, and what is only written down</h2>
			<ul>
				<li>
					<strong>Carried into the prompt.</strong> The signed text below is prepended to Scout’s
					reasoning calls. On a deployment with no signed charter it reaches nothing, and Scout’s
					behaviour is exactly what it was before one existed.
				</li>
				<li>
					<strong>Read by code — four acts, and only these.</strong> A plain <code>DECISION:</code> line
					becomes a durable record; <code>DECISION: CLOSE</code> performs the reviewed close transition
					the Owner’s own command performs; <code>DECISION: OPEN</code> registers an episode and spends
					nothing; <code>DECISION REVERSED:</code> marks a named row reversed.
				</li>
				<li>
					<strong>Everything else is instruction to a model.</strong> The tiers, the “ask once with a
					default” rule and the prohibitions are prose in a prompt. No validator, guard or gate reads
					them, so they are not runtime guarantees and this view does not render them as such.
				</li>
			</ul>
		</section>
	);
}

function CharterPanel() {
	const charter = useMemo(
		() =>
			createLoadable<CharterPayload>(
				() => readGateway('/api/scout/gateway/charter?history=1'),
				expectsMembers('found'),
			),
		[],
	);
	useEffect(() => {
		void charter.load();
	}, [charter]);
	const state = charter.state.value;

	return (
		<section class="view" aria-labelledby="charter-heading">
			<PageHeader
				id="charter-heading"
				title="Charter"
				about={{
					purpose:
						'The document Scout reasons under, as the Owner signed it. It is shown verbatim — including the parts that read badly — because a charter with its contradictions removed is no longer the charter.',
					limits: (
						<p>
							Only four acts in the charter are read by code. Everything else is instruction to a
							model, and this screen does not render it as a runtime guarantee.
						</p>
					),
				}}
			/>
			<ImprovementTabs active="charter" />

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading the charter…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the charter" onRetry={() => void charter.load()} />
			)}
			{state.status === 'ready' && state.data !== null && (
				<>
					{!state.data.found || state.data.charter === null ? (
						<section class="panel" aria-labelledby="charter-none">
							<h2 id="charter-none">No charter is signed</h2>
							<p>
								<strong>This deployment has no signed charter.</strong>{' '}
								<span class="muted">
									That is not the same as an empty one: nothing is carried into Scout’s reasoning, no
									decision lines are recorded from an answer, and its behaviour is what it was before a
									charter existed. A charter is signed by the Owner from the command line; this page
									cannot sign one and offers no control that pretends otherwise.
								</span>
							</p>
						</section>
					) : (
						<section class="panel" aria-labelledby="charter-current">
							<h2 id="charter-current">
								In force — version {state.data.charter.version}
							</h2>
							<dl class="facts">
								<dt>signed</dt>
								<dd>
									<time dateTime={state.data.charter.signedAt}>{state.data.charter.signedAt}</time>
								</dd>
								<dt>content sha256</dt>
								<dd>
									<code>{state.data.charter.contentSha256}</code>{' '}
									<span class="muted">
										— computed server-side from the bytes that were signed, so the text below can be
										checked against it rather than trusted
									</span>
								</dd>
								<dt>note</dt>
								<dd>
									{state.data.charter.note === null || state.data.charter.note.length === 0 ? (
										<span class="muted">none recorded with the signature</span>
									) : (
										state.data.charter.note
									)}
								</dd>
							</dl>
							<pre class="excerpt">{state.data.charter.text}</pre>
						</section>
					)}

					{state.data.versions !== undefined && state.data.versions.length > 0 && (
						<section class="panel" aria-labelledby="charter-history">
							<h2 id="charter-history">Earlier versions</h2>
							<p class="muted small">
								Signing never edits. Each act appends a new immutable version, so the current text is
								always accompanied by the ones it superseded rather than replacing them.
							</p>
							<Bounded shown={state.data.versions.length} noun="versions" />
							<ul class="records">
								{state.data.versions.map((entry) => (
									<li key={entry.charterId} class="record">
										<div class="record-head">
											<code>v{entry.version}</code>
											<span class="tag">{entry.charterId}</span>
										</div>
										<p class="muted small">
											signed <time dateTime={entry.signedAt}>{entry.signedAt}</time> ·{' '}
											<code>{entry.contentSha256.slice(0, 24)}…</code>
										</p>
									</li>
								))}
							</ul>
						</section>
					)}
				</>
			)}

			<EnforcementNote />
		</section>
	);
}

function DecisionsPanel({ anchored = null }: { anchored?: string | null } = {}) {
	const [older, setOlder] = useState<DecisionRecord[]>([]);
	const [more, setMore] = useState(false);
	const [paging, setPaging] = useState(false);
	const [pageError, setPageError] = useState<GatewayError | null>(null);
	const decisions = useMemo(
		() =>
			createLoadable<{ decisions: DecisionRecord[]; hasMore?: boolean }>(
				() => readGateway(`/api/scout/gateway/decisions?limit=${PAGE.decisions}`),
				expectsMembers('decisions'),
			),
		[],
	);
	useEffect(() => {
		setOlder([]);
		setMore(false);
		setPageError(null);
		void decisions.load();
	}, [decisions]);
	const state = decisions.state.value;
	const rows = [...(state.data?.decisions ?? []), ...older];
	const hasMore = more || (older.length === 0 && state.data?.hasMore === true);
	const loadMore = useCallback(async () => {
		if (state.status !== 'ready' || state.data === null) return;
		setPaging(true);
		setPageError(null);
		const offset = state.data.decisions.length + older.length;
		const result = await readGateway<{ decisions: DecisionRecord[]; hasMore?: boolean }>(
			`/api/scout/gateway/decisions?limit=${PAGE.decisions}&offset=${offset}`,
		);
		setPaging(false);
		if (!result.ok) {
			setPageError(result.error);
			return;
		}
		setOlder((previous) => [...previous, ...result.data.decisions]);
		setMore(result.data.hasMore === true);
	}, [state, older]);

	return (
		<section class="view" aria-labelledby="decisions-heading">
			<PageHeader
				id="decisions-heading"
				title="Decisions"
				about={{
					purpose:
						'What Scout recorded as decided under its charter. These are Scout’s own declarations — a record that it has settled something, not an approval from anyone and not a change to anything in the world.',
					limits: (
						<p>
							A declared decision is not an Owner decision and not an experiment. The authority each
							row carries is stated beside it.
						</p>
					),
				}}
			/>
			<ImprovementTabs active="decisions" />

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading the decisions…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the decisions" onRetry={() => void decisions.load()} />
			)}
			{state.status === 'ready' && state.data !== null && (
				<section class="panel" aria-labelledby="decisions-list">
					<h2 id="decisions-list">On the record</h2>
					{rows.length === 0 ? (
						<p>
							<strong>No decision is on record.</strong>{' '}
							<span class="muted">
								A decision is recorded when Scout answers under a signed charter and declares one, or
								when a closed episode resolves it. An empty list here means none has been recorded, not
								that one is hidden.
							</span>
						</p>
					) : (
						<>
							<Bounded shown={rows.length} noun="decisions" />
							<Anchored
								anchored={anchored}
								present={rows.some((entry) => entry.decisionId === anchored)}
								noun="decision(s)"
								shown={rows.length}
							/>
							<ul class="records">
								{rows.map((entry) => (
									<li
										key={entry.decisionId}
										class={`record${entry.decisionId === anchored ? ' is-anchored' : ''}`}
										aria-current={entry.decisionId === anchored ? ('true' as const) : undefined}
									>
										<div class="record-head">
											<code>{entry.decisionId}</code>
											{entry.reversedAt === null ? (
												<span class="tag tag-live">standing</span>
											) : (
												<span class="tag tag-warn">reversed</span>
											)}
											{entry.outcome !== null && <span class="tag">outcome {entry.outcome}</span>}
										</div>
										<p class="record-summary">{entry.statement}</p>
										<p class="muted small">{entry.basis}</p>
										<p class="muted small">
											recorded <time dateTime={entry.createdAt}>{entry.createdAt}</time>
											{entry.episodeId !== null && (
												<>
													{' '}
													· episode <code>{entry.episodeId}</code>{' '}
													{/* THE DECISION ITSELF HAS NO CANONICAL REF KIND — `dec:…`
													    resolves to UNKNOWN_KIND — so the link is offered for the
													    RECORD IT WAS DECIDED AGAINST, which does resolve.
													    Offering one for the decision would be offering a
													    citation the reader knows cannot answer. */}
													<DiscussLink
														ref={`research:${entry.episodeId}`}
														label="Discuss the episode"
													/>
												</>
											)}
										</p>
										{entry.reversedAt !== null && (
											<p class="small">
												<span class="tag tag-warn">reversed</span>{' '}
												<time dateTime={entry.reversedAt}>{entry.reversedAt}</time>
												{entry.reversalNote !== null && entry.reversalNote.length > 0 && (
													<>
														{' '}
														— <span class="muted">{entry.reversalNote}</span>
													</>
												)}
											</p>
										)}
									</li>
								))}
							</ul>
							{hasMore && (
								<button
									type="button"
									class="button ghost"
									disabled={paging}
									onClick={() => void loadMore()}
								>
									{paging ? 'Reading the next page…' : 'Load more decisions'}
								</button>
							)}
							{pageError !== null && (
								<p class="notice small" role="status">
									The next page of decisions could not be read: {pageError.message}
								</p>
							)}
						</>
					)}
				</section>
			)}

			<section class="panel not-known" aria-labelledby="decisions-authority">
				<h2 id="decisions-authority">Whose authority each of these carries</h2>
				<ul>
					<li>
						<strong>These are Scout’s declarations, not the Owner’s approval.</strong> A decision row
						records that Scout settled something under its charter. It carries no field saying an Owner
						authorized it, because none exists — and this view will not imply one.
					</li>
					<li>
						<strong>A scientific conclusion is not a code change.</strong> Nothing here authorizes a
						change to any system, and nothing here is a deployment. Scout has no hands: it observes,
						reasons and records.
					</li>
					<li>
						<strong>Reversal is recorded on the row it reverses.</strong> The statement is never
						rewritten and there is no second reversal — the original stands beside the note that
						overturned it, which is why both appear together above.
					</li>
				</ul>
			</section>
		</section>
	);
}

const LIFECYCLE_LABELS: Record<string, { label: string; tone: string }> = {
	SUGGESTED: { label: 'suggested', tone: '' },
	REVIEWED: { label: 'reviewed', tone: 'tag-live' },
	IMPLEMENTED: { label: 'implemented', tone: 'tag-live' },
	VERIFIED: { label: 'verified', tone: 'tag-live' },
	DISMISSED: { label: 'dismissed', tone: 'tag-warn' },
	DEFERRED: { label: 'deferred', tone: 'tag-warn' },
};

function LifecycleTag({ lifecycle }: { lifecycle: string | undefined }) {
	const entry = LIFECYCLE_LABELS[lifecycle ?? 'SUGGESTED'] ?? LIFECYCLE_LABELS.SUGGESTED;
	return <span class={`tag ${entry.tone}`.trim()}>{entry.label}</span>;
}

function episodePathFor(ref: string): string {
	let id = ref;
	if (id.startsWith('research:')) id = id.slice('research:'.length);
	else if (id.startsWith('episode:')) id = id.slice('episode:'.length);
	return `/research/observatory/${encodeURIComponent(id)}`;
}

/**
 * THE OWNER'S ACTIONS ON ONE CANDIDATE.
 *
 * THE FORM IS THE EVIDENCE REQUIREMENT MADE VISIBLE. The server refuses a
 * review with no reason, an implementation with no change reference, and a
 * verification with no method or result — so this control asks for exactly
 * those things before the request is made. A button that sent an empty body
 * would be a button that could not succeed.
 *
 * IT NEVER TOUCHES NOTIFICATION READ STATE. Reading an inbox entry is not a
 * review, and nothing here writes one.
 */
function CandidateActions({
	proposal,
	onRecorded,
}: {
	proposal: Proposal;
	onRecorded: () => void;
}) {
	const [mode, setMode] = useState<'none' | 'review' | 'implementation' | 'verification'>('none');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<GatewayError | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [decision, setDecision] = useState('ACCEPTED');
	const [reason, setReason] = useState('');
	const [description, setDescription] = useState('');
	const [commitSha, setCommitSha] = useState('');
	const [deployedVersion, setDeployedVersion] = useState('');
	const [configRef, setConfigRef] = useState('');
	const [changeRef, setChangeRef] = useState('');
	const [actor, setActor] = useState('');
	const [method, setMethod] = useState('');
	const [testRef, setTestRef] = useState('');
	const [evaluationRef, setEvaluationRef] = useState('');
	const [beforeAfter, setBeforeAfter] = useState('');
	const [result, setResult] = useState('');

	const candidateId = proposal.candidateId;
	if (candidateId === null) return null;

	async function submit(path: string, body: Record<string, unknown>, what: string): Promise<void> {
		setBusy(true);
		setError(null);
		setNotice(null);
		const response = await readGateway(path, { method: 'POST', body });
		setBusy(false);
		if (!response.ok) {
			setError(response.error);
			return;
		}
		setNotice(what);
		setMode('none');
		onRecorded();
	}

	const canImplement =
		description.trim().length > 0 &&
		actor.trim().length > 0 &&
		[commitSha, deployedVersion, configRef, changeRef].some((value) => value.trim().length > 0);
	const canVerify =
		method.trim().length > 0 &&
		result.trim().length > 0 &&
		actor.trim().length > 0 &&
		[testRef, evaluationRef, beforeAfter].some((value) => value.trim().length > 0);

	return (
		<div class="candidate-actions">
			<div class="filterbar">
				<button
					type="button"
					class={`button${mode === 'review' ? ' is-active' : ''}`}
					onClick={() => setMode(mode === 'review' ? 'none' : 'review')}
				>
					Review
				</button>
				<button
					type="button"
					class={`button${mode === 'implementation' ? ' is-active' : ''}`}
					onClick={() => setMode(mode === 'implementation' ? 'none' : 'implementation')}
				>
					Mark as Implemented
				</button>
				<button
					type="button"
					class={`button${mode === 'verification' ? ' is-active' : ''}`}
					onClick={() => setMode(mode === 'verification' ? 'none' : 'verification')}
				>
					Mark as Verified
				</button>
			</div>

			{mode === 'review' && (
				<form
					class="candidate-form"
					onSubmit={(event: Event) => {
						event.preventDefault();
						void submit(
							`/api/scout/gateway/improvements/${encodeURIComponent(candidateId)}/review`,
							{ decision, reason },
							'The review decision was recorded.',
						);
					}}
				>
					<label>
						Decision
						<select value={decision} onChange={(event) => setDecision((event.target as HTMLSelectElement).value)}>
							<option value="ACCEPTED">Accepted</option>
							<option value="PARTIALLY_ACCEPTED">Partially accepted</option>
							<option value="DEFERRED">Deferred</option>
							<option value="DISMISSED">Dismissed</option>
						</select>
					</label>
					<label>
						Why
						<textarea
							rows={3}
							value={reason}
							onInput={(event) => setReason((event.target as HTMLTextAreaElement).value)}
							placeholder="What did you decide, and on what grounds?"
						/>
					</label>
					<button type="submit" class="button" disabled={busy || reason.trim().length === 0}>
						{busy ? 'Recording…' : 'Record review'}
					</button>
				</form>
			)}

			{mode === 'implementation' && (
				<form
					class="candidate-form"
					onSubmit={(event: Event) => {
						event.preventDefault();
						if (!canImplement) return;
						void submit(
							`/api/scout/gateway/improvements/${encodeURIComponent(candidateId)}/implementation`,
							{ description, actor, commitSha, deployedVersion, configRef, changeRef },
							'The implementation evidence was recorded.',
						);
					}}
				>
					<p class="muted small">
						Implemented is a claim with evidence. At least one change reference is required, and
						the candidate must have been accepted or partially accepted first.
					</p>
					<label>
						What changed
						<textarea
							rows={2}
							value={description}
							onInput={(event) => setDescription((event.target as HTMLTextAreaElement).value)}
							placeholder="One sentence describing the change."
						/>
					</label>
					<label>
						Actor
						<input
							type="text"
							value={actor}
							onInput={(event) => setActor((event.target as HTMLInputElement).value)}
							placeholder="Who made the change."
						/>
					</label>
					<div class="candidate-form-grid">
						<label>
							Commit SHA
							<input type="text" value={commitSha} onInput={(event) => setCommitSha((event.target as HTMLInputElement).value)} />
						</label>
						<label>
							Deployed version
							<input type="text" value={deployedVersion} onInput={(event) => setDeployedVersion((event.target as HTMLInputElement).value)} />
						</label>
						<label>
							Configuration change
							<input type="text" value={configRef} onInput={(event) => setConfigRef((event.target as HTMLInputElement).value)} />
						</label>
						<label>
							Related change record
							<input type="text" value={changeRef} onInput={(event) => setChangeRef((event.target as HTMLInputElement).value)} />
						</label>
					</div>
					<button type="submit" class="button" disabled={busy || !canImplement}>
						{busy ? 'Recording…' : 'Record implementation'}
					</button>
				</form>
			)}

			{mode === 'verification' && (
				<form
					class="candidate-form"
					onSubmit={(event: Event) => {
						event.preventDefault();
						if (!canVerify) return;
						void submit(
							`/api/scout/gateway/improvements/${encodeURIComponent(candidateId)}/verification`,
							{ method, result, actor, testRef, evaluationRef, beforeAfter },
							'The verification evidence was recorded.',
						);
					}}
				>
					<p class="muted small">
						Verified is not deployed. Verification needs its own method and result — a real test
						run, an evaluation, or a before/after comparison.
					</p>
					<label>
						Method
						<textarea
							rows={2}
							value={method}
							onInput={(event) => setMethod((event.target as HTMLTextAreaElement).value)}
							placeholder="What was run, and against what?"
						/>
					</label>
					<label>
						Result
						<textarea
							rows={2}
							value={result}
							onInput={(event) => setResult((event.target as HTMLTextAreaElement).value)}
							placeholder="What did it show?"
						/>
					</label>
					<label>
						Actor
						<input
							type="text"
							value={actor}
							onInput={(event) => setActor((event.target as HTMLInputElement).value)}
							placeholder="Who ran the verification."
						/>
					</label>
					<div class="candidate-form-grid">
						<label>
							Test reference
							<input type="text" value={testRef} onInput={(event) => setTestRef((event.target as HTMLInputElement).value)} />
						</label>
						<label>
							Evaluation reference
							<input type="text" value={evaluationRef} onInput={(event) => setEvaluationRef((event.target as HTMLInputElement).value)} />
						</label>
						<label>
							Before / after
							<input type="text" value={beforeAfter} onInput={(event) => setBeforeAfter((event.target as HTMLInputElement).value)} />
						</label>
					</div>
					<button type="submit" class="button" disabled={busy || !canVerify}>
						{busy ? 'Recording…' : 'Record verification'}
					</button>
				</form>
			)}

			{notice !== null && (
				<p class="small" role="status">
					{notice}
				</p>
			)}
			{error !== null && (
				<p class="small" role="alert">
					<strong>Not recorded.</strong> {error.message}{' '}
					<span class="muted">
						(<code>{error.name}</code>)
					</span>
				</p>
			)}
		</div>
	);
}

function ImprovementTabs({ active }: { active: 'proposals' | 'charter' | 'decisions' }) {
	const tabs = [
		{ id: 'proposals' as const, label: 'Proposals', path: '/workspace/improvements' },
		{ id: 'charter' as const, label: 'Charter', path: '/workspace/charter' },
		{ id: 'decisions' as const, label: 'Decisions', path: '/workspace/decisions' },
	];
	return (
		<nav class="area-tabs improvement-tabs" aria-label="Improvements views">
			{tabs.map((tab) => (
				<a
					key={tab.id}
					href={tab.path}
					class={`area-tab${tab.id === active ? ' is-current' : ''}`}
					aria-current={tab.id === active ? ('page' as const) : undefined}
					onClick={(event: MouseEvent) => {
						if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
						event.preventDefault();
						navigate({
							destination: 'workspace',
							section: tab.id === 'proposals' ? 'improvements' : tab.id,
							recordId: null,
						});
					}}
				>
					{tab.label}
				</a>
			))}
		</nav>
	);
}

function ProposalsPanel({ anchored = null }: { anchored?: string | null } = {}) {
	const proposals = useMemo(
		() =>
			createLoadable<{ candidates: Proposal[]; critiques: Critique[]; note?: string }>(
				() => readGateway('/api/scout/gateway/self-improvement'),
				expectsMembers('candidates', 'critiques'),
			),
		[],
	);
	useEffect(() => {
		void proposals.load();
	}, [proposals]);
	const state = proposals.state.value;
	// A CANDIDATE ID MAY BE FULL OR A UNIQUE SHORT PREFIX. Scout's prose
	// sometimes cites the 8-hex prefix; the record still has to open. The
	// self-improvement id remains the DOM anchor.
	const matchesAnchor = (proposal: Proposal): boolean =>
		proposal.selfImprovementId === anchored ||
		proposal.candidateId === anchored ||
		(anchored !== null &&
			anchored.startsWith('scoutcand_') &&
			proposal.candidateId !== null &&
			proposal.candidateId.startsWith(`${anchored}-`));
	useEffect(() => {
		if (state.status !== 'ready' || state.data === null || anchored === null) return;
		const proposal = state.data.candidates.find(matchesAnchor);
		if (proposal === undefined) return;
		document
			.getElementById(`proposal-${proposal.selfImprovementId}`)
			?.scrollIntoView({ block: 'start' });
	}, [state.status, state.data, anchored]);

	return (
		<section class="view" aria-labelledby="proposals-heading">
			<PageHeader
				id="proposals-heading"
				title="Improvements"
				about={{
					purpose:
						'What Scout thinks should improve, why it thinks so, and how far the Owner’s review, implementation and verification have actually got.',
					how: (
						<>
							<p>
								Each proposal is linked to the research episode and self-criticism that motivated
								it. The Owner’s review decision, the implementation evidence and the verification
								evidence are separate, ordered stages: nothing skips a step, and a commit is not a
								verification.
							</p>
							<p>
								Scout cannot carry out any of these. It has no hands, it cannot edit its own source,
								and the controls here are the Owner’s.
							</p>
						</>
					),
					limits: (
						<>
							<p>
								<strong>A marked notification is not a review, and a review is not an
								implementation.</strong> The lifecycle reads the candidate’s own evidence, never an
								inbox row’s read state.
							</p>
							<p>
								<strong>Whether the benefit is real is a later question.</strong> Verification says
								the intended problem was checked by a stated method; it does not prove the change
								helped in general.
							</p>
						</>
					),
				}}
			/>
			<ImprovementTabs active="proposals" />

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading the proposals…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the proposals" onRetry={() => void proposals.load()} />
			)}
			{state.status === 'ready' && state.data !== null && (
				<>
					{state.data.candidates.length === 0 ? (
						<p>
							<strong>No proposal is on record.</strong>{' '}
							<span class="muted">
								A proposal is written when Scout criticises its own work and the criticism survives
								into an improvement shape. An empty list here means none has been written, not that one
								is hidden.
							</span>
						</p>
					) : (
						<section class="panel" aria-labelledby="proposals-list">
							<h2 id="proposals-list">Proposed</h2>
							<Bounded shown={state.data.candidates.length} noun="proposals" />
							<Anchored
								anchored={anchored}
								present={state.data.candidates.some(matchesAnchor)}
								noun="proposal(s)"
								shown={state.data.candidates.length}
							/>
							{/* THE INDEX IS THE SCANNABLE SURFACE; the records below keep
							    the full argument, evidence and Owner controls. Selecting a
							    row is an anchor, so it works without JavaScript and does not
							    pretend to be a second route. */}
							<div class="table-wrap proposal-index">
								<table class="data-table">
									<thead>
										<tr>
											<th scope="col">Proposal</th>
											<th scope="col">Lifecycle</th>
											<th scope="col">Benefit</th>
											<th scope="col">Confidence</th>
											<th scope="col">Recorded</th>
										</tr>
									</thead>
									<tbody>
										{state.data.candidates.map((proposal) => (
											<tr
												key={`index:${proposal.selfImprovementId}`}
												class="row-link"
												onClick={() => {
													document
														.getElementById(`proposal-${proposal.selfImprovementId}`)
														?.scrollIntoView({ block: 'start' });
												}}
											>
												<td class="cell-title">
													<a href={`#proposal-${proposal.selfImprovementId}`}>
														{proposal.observedWeakness}
													</a>
													<span class="cell-sub">{proposal.expectedBenefit}</span>
												</td>
												<td data-label="Lifecycle">
													<LifecycleTag lifecycle={proposal.lifecycle} />
												</td>
												<td class="cell-muted" data-label="Benefit">
													{proposal.expectedBenefitClass}
												</td>
												<td class="cell-muted" data-label="Confidence">
													{proposal.confidence}
												</td>
												<td class="cell-date" data-label="Recorded">
													{formatWhen(proposal.createdAt)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<ul class="records proposal-records">
								{state.data.candidates.map((proposal) => {
									const isAnchored = matchesAnchor(proposal);
									return (
										<li
											key={proposal.selfImprovementId}
											id={`proposal-${proposal.selfImprovementId}`}
											class={`record${isAnchored ? ' is-anchored' : ''}`}
											aria-current={isAnchored ? ('true' as const) : undefined}
										>
											<div class="record-head">
												<span class="record-title">{proposal.observedWeakness}</span>
												<LifecycleTag lifecycle={proposal.lifecycle} />
												<span class="tag">confidence {proposal.confidence}</span>
												<span class="tag">benefit {proposal.expectedBenefitClass}</span>
												{proposal.authoritySeeking && (
													<span class="tag tag-warn">authority needed</span>
												)}
											</div>
											<dl class="facts">
												<dt>expected benefit</dt>
												<dd>{proposal.expectedBenefit}</dd>
												<dt>how it would be evaluated</dt>
												<dd>{proposal.evaluationMethod}</dd>
												<dt>alternatives considered</dt>
												<dd>
													{proposal.alternatives.length === 0 ? (
														<span class="muted">none recorded</span>
													) : (
														proposal.alternatives.join(' · ')
													)}
												</dd>
												<dt>applicability</dt>
												<dd>{proposal.applicability.join(' · ')}</dd>
											</dl>
											{proposal.authoritySeeking && proposal.authorityReason !== null && (
												<p class="small">
													<span class="tag tag-warn">authority needed</span>{' '}
													<span class="muted">{proposal.authorityReason}</span>
												</p>
											)}

											{proposal.review !== null && proposal.review !== undefined && (
												<p class="small">
													<strong>Review:</strong> {proposal.review.decision.replace('_', ' ')} —{' '}
													{proposal.review.reason}{' '}
													<span class="muted">({proposal.review.actor})</span>
												</p>
											)}
											{proposal.implementationEvidence !== null &&
												proposal.implementationEvidence !== undefined && (
													<Technical summary="Implementation evidence">
														<p class="small">
															{proposal.implementationEvidence.description} —{' '}
															{proposal.implementationEvidence.actor}
														</p>
														<ul class="small">
															{proposal.implementationEvidence.commitSha !== null && (
																<li>
																	commit <code>{proposal.implementationEvidence.commitSha}</code>
																</li>
															)}
															{proposal.implementationEvidence.deployedVersion !== null && (
																<li>
																	deployed version{' '}
																	<code>{proposal.implementationEvidence.deployedVersion}</code>
																</li>
															)}
															{proposal.implementationEvidence.configRef !== null && (
																<li>
																	configuration{' '}
																	<code>{proposal.implementationEvidence.configRef}</code>
																</li>
															)}
															{proposal.implementationEvidence.changeRef !== null && (
																<li>
																	change record{' '}
																	<code>{proposal.implementationEvidence.changeRef}</code>
																</li>
															)}
														</ul>
													</Technical>
												)}
											{proposal.verificationEvidence !== null &&
												proposal.verificationEvidence !== undefined && (
													<Technical summary="Verification evidence">
														<p class="small">
															<strong>{proposal.verificationEvidence.method}</strong> —{' '}
															{proposal.verificationEvidence.result} (
															{proposal.verificationEvidence.actor})
														</p>
														<ul class="small">
															{proposal.verificationEvidence.testRef !== null && (
																<li>
																	test <code>{proposal.verificationEvidence.testRef}</code>
																</li>
															)}
															{proposal.verificationEvidence.evaluationRef !== null && (
																<li>
																	evaluation{' '}
																	<code>{proposal.verificationEvidence.evaluationRef}</code>
																</li>
															)}
															{proposal.verificationEvidence.beforeAfter !== null && (
																<li>before/after: {proposal.verificationEvidence.beforeAfter}</li>
															)}
														</ul>
													</Technical>
												)}
											{proposal.stage !== null && proposal.stage !== undefined && (
												<Technical summary={`Stage: ${proposal.stage.reached.toLowerCase().replace(/_/g, ' ')}`}>
													<p class="small">{proposal.stage.establishes}</p>
													<p class="small muted">{proposal.stage.doesNotEstablish}</p>
													{proposal.stage.nextRequires !== null && (
														<p class="small">Next needs: {proposal.stage.nextRequires}</p>
													)}
													{proposal.stage.unaccountedFor.map((line) => (
														<p key={line} class="small">
															<span class="tag tag-warn">unaccounted</span> {line}
														</p>
													))}
												</Technical>
											)}

											{(proposal.linkedEpisodes ?? []).length > 0 && (
												<p class="small">
													<strong>Motivating investigation:</strong>{' '}
													{(proposal.linkedEpisodes ?? []).map((ref) => (
														<a
															key={ref}
															href={episodePathFor(ref)}
															onClick={(event: MouseEvent) => {
																if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
																event.preventDefault();
																navigate({
																	destination: 'research',
																	section: 'observatory',
																	recordId: ref
																		.replace(/^research:/, '')
																		.replace(/^episode:/, ''),
																});
															}}
														>
															Open the record
														</a>
													))}
												</p>
											)}

											<p class="muted small">
												recorded <time dateTime={proposal.createdAt}>{formatWhen(proposal.createdAt)}</time> ·
												evidence {proposal.evidenceRefs.length} ref(s) · critiques{' '}
												{proposal.critiqueRefs.length}
											</p>
											{proposal.evidenceRefs.length > 0 && (
												<p class="small">
													{proposal.evidenceRefs.map((ref) => (
														<span key={ref}>
															<DiscussLink ref={ref} label="Discuss the evidence" />{' '}
														</span>
													))}
												</p>
											)}

											<Technical summary="Technical record">
												<p class="small">
													proposal <code>{proposal.selfImprovementId}</code>
												</p>
												<p class="small">
													ledger entry{' '}
													{proposal.candidateId === null ? (
														<span class="muted">
															none — this proposal was not entered in the ledger because it
															seeks authority
														</span>
													) : (
														<code>{proposal.candidateId}</code>
													)}
												</p>
											</Technical>
											<CandidateActions proposal={proposal} onRecorded={() => void proposals.load()} />
										</li>
									);
								})}
							</ul>
						</section>
					)}

					{state.data.critiques.length > 0 && (
						<section class="panel" aria-labelledby="critiques-list">
							<h2 id="critiques-list">Unresolved self-criticism</h2>
							<Bounded shown={state.data.critiques.length} noun="critiques" />
							<ul class="records">
								{state.data.critiques.map((critique) => (
									<li
										key={critique.critiqueId}
										class={`record${critique.critiqueId === anchored ? ' is-anchored' : ''}`}
										aria-current={critique.critiqueId === anchored ? ('true' as const) : undefined}
									>
										<div class="record-head">
											<span class="record-title">{critique.statement}</span>
											<span class="tag">{critique.kind}</span>
											<span class="tag">confidence {critique.confidence}</span>
										</div>
										<p class="small">
											<strong>What would resolve it:</strong> {critique.resolutionTest}
										</p>
										<p class="muted small">
											recorded <time dateTime={critique.createdAt}>{formatWhen(critique.createdAt)}</time>
										</p>
										<Technical summary="Critique identifier">
											<code>{critique.critiqueId}</code>
										</Technical>
										{critique.evidenceRefs.length > 0 && (
											<p class="small">
												{critique.evidenceRefs.map((ref) => (
													<span key={ref}>
														<DiscussLink ref={ref} label="Discuss the evidence" />{' '}
													</span>
												))}
											</p>
										)}
									</li>
								))}
							</ul>
						</section>
					)}
				</>
			)}
		</section>
	);
}

/**
 * THE DESTINATION'S ONE ENTRY POINT. The path picks the surface, exactly as
 * `/chat/discuss/<ref>` picks the chat's — so each is deep-linkable and survives
 * a reload. `/improvements` is the proposals; the charter and the decisions have
 * paths of their own rather than being tabs inside it, because a link to "the
 * charter" should be a link to the charter.
 */
export function ImprovementsView() {
	const current = route.value;
	if (current.section === 'charter') return <CharterPanel />;
	if (current.section === 'decisions' || current.section === 'decision') {
		return <DecisionsPanel anchored={current.recordId} />;
	}
	// ONE TAB, THREE ANCHORED ADDRESSES. A search result names a proposal, a
	// criticism or a candidate; the proposals panel marks the row and says so
	// when it is outside this bounded page. There is no separate candidate
	// surface to land on — the candidate IS the proposal's ledger entry.
	if (
		current.section === 'proposal' ||
		current.section === 'critique' ||
		current.section === 'improvements'
	) {
		return <ProposalsPanel anchored={current.recordId} />;
	}
	return <ProposalsPanel />;
}

/**
 * THE ROW A LINK ASKED FOR, OR THE REASON IT IS NOT HERE.
 *
 * IT NEVER SAYS "NOT FOUND". A record missing from the pages loaded so far is
 * missing from a page, not from the record — the list now continues with Load
 * more, and reporting "not in these pages yet" as absence would be the product
 * telling the Owner a decision does not exist when it does. The id travels with
 * the sentence so the record can still be identified, and the search result the
 * Owner arrived from carries the record's own words.
 */
function Anchored({
	anchored,
	present,
	noun,
	shown,
}: {
	anchored: string | null;
	present: boolean;
	noun: string;
	shown: number;
}) {
	if (anchored === null) return null;
	if (present) {
		return (
			<p class="notice small">
				<strong>Opened from a search.</strong>{' '}
				<span class="muted">
					<code>{anchored}</code> is marked below.
				</span>
			</p>
		);
	}
	return (
		<p class="notice small">
			<strong>
				<code>{anchored}</code> is not in the {shown} {noun} this read returns.
			</strong>{' '}
			<span class="muted">
				It may be on a later page — the list below continues with Load more — and the id above is
				the one the search found; the search result carries the record’s own words.
			</span>
		</p>
	);
}
